import { useCallback, useEffect, useMemo, useState, type FormEvent, type JSX } from 'react';

import { getDefaultApiContext } from '../../api/client.js';
import {
  createAdminUserApi,
  deleteAdminUserApi,
  listAdminUsersApi,
  resetWorkspaceApi,
  type AdminUser,
  type AuthRole,
} from '../../api/clients/auth.js';
import { useAuthStore } from '../../state/auth.js';

interface AddUserFormState {
  username: string;
  password: string;
  role: AuthRole;
}

const EMPTY_FORM: AddUserFormState = { username: '', password: '', role: 'user' };

export function AdminPanel(props: { onClose: () => void }): JSX.Element {
  const me = useAuthStore((s) => s.user);
  const [users, setUsers] = useState<AdminUser[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState<AddUserFormState>(EMPTY_FORM);
  const [resetConfirm, setResetConfirm] = useState('');
  const [resetBusy, setResetBusy] = useState(false);
  const [resetMessage, setResetMessage] = useState<
    { kind: 'success'; text: string } | { kind: 'error'; text: string } | null
  >(null);

  const ctx = useMemo(() => getDefaultApiContext(), []);

  const reload = useCallback(async (): Promise<void> => {
    setError(null);
    const res = await listAdminUsersApi(ctx);
    if (res.ok) setUsers(res.value.users);
    else setError(res.error.message);
  }, [ctx]);

  useEffect(() => {
    void reload();
  }, [reload]);

  async function onAdd(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const res = await createAdminUserApi(ctx, form);
    setBusy(false);
    if (res.ok) {
      setForm(EMPTY_FORM);
      await reload();
    } else {
      setError(res.error.message);
    }
  }

  async function onDelete(id: string): Promise<void> {
    if (!confirm('Delete this user? Their chat sessions will be removed too.')) return;
    setBusy(true);
    setError(null);
    const res = await deleteAdminUserApi(ctx, id);
    setBusy(false);
    if (res.ok) {
      await reload();
    } else {
      setError(res.error.message);
    }
  }

  async function onResetWorkspace(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setResetBusy(true);
    setResetMessage(null);
    const res = await resetWorkspaceApi(ctx, { confirmWorkspaceName: resetConfirm });
    setResetBusy(false);
    if (res.ok) {
      setResetMessage({
        kind: 'success',
        text: `Workspace reset complete — restored from ${res.value.fixtureSource}. Wiped ${res.value.wiped.length} top-level entries.`,
      });
      setResetConfirm('');
    } else {
      setResetMessage({ kind: 'error', text: res.error.message });
    }
  }

  return (
    <div className="bg-cream-50 text-fg min-h-screen p-8">
      <header className="mb-8 flex items-center justify-between">
        <div className="flex flex-col gap-1">
          <span className="text-audi-red text-[11px] font-semibold uppercase tracking-[0.22em]">
            Administration
          </span>
          <h1 className="text-fg text-3xl font-extrabold leading-[1.1] tracking-[-0.02em]">
            User Management
          </h1>
        </div>
        <button
          type="button"
          className="border-taupe-500 text-fg hover:border-taupe-900 hover:bg-cream-100 rounded-none border px-4 py-2 text-xs font-semibold uppercase tracking-wide"
          onClick={props.onClose}
        >
          ← Back to TESSA
        </button>
      </header>

      {error !== null ? (
        <div
          role="alert"
          className="border-audi-red bg-accent-red-bg text-audi-red mb-4 rounded-none border px-3 py-2 text-sm"
        >
          {error}
        </div>
      ) : null}

      <section className="border-taupe-300 bg-cream-50 mb-8 rounded-none border p-5">
        <h2 className="text-fg mb-4 text-xs font-semibold uppercase tracking-[0.18em]">
          Add a user
        </h2>
        <form
          onSubmit={(e) => {
            void onAdd(e);
          }}
          className="grid grid-cols-1 items-end gap-3 md:grid-cols-4"
        >
          <label className="block text-sm">
            <span className="text-taupe-700 mb-1 block text-[11px] font-semibold uppercase tracking-[0.12em]">
              Username
            </span>
            <input
              type="text"
              value={form.username}
              onChange={(e) => setForm({ ...form, username: e.target.value })}
              disabled={busy}
              className="bg-cream-50 border-taupe-500 text-fg focus:border-audi-red w-full rounded-none border px-3 py-2 focus:outline-none focus:ring-1 focus:ring-audi-red"
              required
              minLength={3}
              maxLength={64}
              pattern="[A-Za-z0-9._\-]+"
            />
          </label>
          <label className="block text-sm">
            <span className="text-taupe-700 mb-1 block text-[11px] font-semibold uppercase tracking-[0.12em]">
              Password
            </span>
            <input
              type="password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              disabled={busy}
              className="bg-cream-50 border-taupe-500 text-fg focus:border-audi-red w-full rounded-none border px-3 py-2 focus:outline-none focus:ring-1 focus:ring-audi-red"
              required
              minLength={8}
            />
          </label>
          <label className="block text-sm">
            <span className="text-taupe-700 mb-1 block text-[11px] font-semibold uppercase tracking-[0.12em]">
              Role
            </span>
            <select
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value as AuthRole })}
              disabled={busy}
              className="bg-cream-50 border-taupe-500 text-fg focus:border-audi-red w-full rounded-none border px-3 py-2 focus:outline-none focus:ring-1 focus:ring-audi-red"
            >
              <option value="user">user</option>
              <option value="admin">admin</option>
            </select>
          </label>
          <button
            type="submit"
            disabled={busy}
            className="bg-taupe-900 hover:bg-audi-red rounded-none px-4 py-2 text-xs font-semibold uppercase tracking-wide text-white disabled:opacity-50"
          >
            Add user
          </button>
        </form>
      </section>

      <section className="border-taupe-300 bg-cream-50 rounded-none border p-5">
        <h2 className="text-fg mb-4 text-xs font-semibold uppercase tracking-[0.18em]">
          Users
        </h2>
        {users === null ? (
          <p className="text-taupe-700 text-sm">Loading…</p>
        ) : users.length === 0 ? (
          <p className="text-taupe-700 text-sm">No users yet.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-taupe-500 text-taupe-700 border-b text-left text-[11px] uppercase tracking-wide">
                <th className="py-2 pr-3 font-semibold">Username</th>
                <th className="py-2 pr-3 font-semibold">Role</th>
                <th className="py-2 pr-3 font-semibold">Last login</th>
                <th className="py-2 pr-3 font-semibold">Sessions</th>
                <th className="py-2 pr-3 font-semibold">Tokens</th>
                <th className="py-2 pr-3 font-semibold">Last workspace</th>
                <th className="py-2"></th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const isMe = me?.id === u.id;
                return (
                  <tr
                    key={u.id}
                    className="border-taupe-300 hover:bg-cream-100 border-b last:border-0"
                  >
                    <td className="py-2 pr-3">
                      {u.username}
                      {isMe ? (
                        <span className="text-audi-red ml-2 text-xs uppercase tracking-wide">
                          (you)
                        </span>
                      ) : null}
                    </td>
                    <td className="py-2 pr-3">
                      <span
                        className={`inline-block rounded-none px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
                          u.role === 'admin'
                            ? 'border-audi-red text-audi-red border'
                            : 'border-taupe-500 text-taupe-700 border'
                        }`}
                      >
                        {u.role}
                      </span>
                    </td>
                    <td className="text-taupe-700 py-2 pr-3">
                      {u.lastLogin !== null ? new Date(u.lastLogin).toLocaleString() : '—'}
                    </td>
                    <td className="text-taupe-700 py-2 pr-3 font-mono">{u.sessionCount}</td>
                    <td className="text-taupe-700 py-2 pr-3 font-mono">
                      {u.tokenSpend.toLocaleString()}
                    </td>
                    <td
                      className="text-taupe-700 max-w-[24ch] truncate py-2 pr-3"
                      title={u.lastWorkspace ?? ''}
                    >
                      {u.lastWorkspace ?? '—'}
                    </td>
                    <td className="py-2 text-right">
                      <button
                        type="button"
                        disabled={busy || isMe}
                        onClick={() => {
                          void onDelete(u.id);
                        }}
                        className="border-audi-red text-audi-red hover:bg-accent-red-bg rounded-none border px-2 py-1 text-[11px] font-semibold uppercase tracking-wide disabled:cursor-not-allowed disabled:opacity-30"
                        title={isMe ? 'You cannot delete yourself' : 'Delete user'}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </section>

      <section
        className="border-audi-red bg-cream-50 mt-8 rounded-none border p-5"
        data-test="admin-reset-workspace-section"
      >
        <h2 className="text-audi-red mb-2 text-xs font-semibold uppercase tracking-[0.18em]">
          Reset workspace to default fixture
        </h2>
        <p className="text-taupe-700 mb-4 text-sm">
          Wipes the current workspace and restores the canonical{' '}
          <code className="bg-cream-100 px-1">e2e/fixtures/debug-workspace/</code> fixture.
          User accounts are preserved; chat sessions, views, and pending changesets are wiped.
          This action is destructive and intended for testing only.
        </p>

        {resetMessage !== null ? (
          <div
            role="alert"
            data-test="admin-reset-workspace-message"
            className={
              resetMessage.kind === 'success'
                ? 'border-taupe-500 bg-cream-100 text-fg mb-4 rounded-none border px-3 py-2 text-sm'
                : 'border-audi-red bg-accent-red-bg text-audi-red mb-4 rounded-none border px-3 py-2 text-sm'
            }
          >
            {resetMessage.text}
          </div>
        ) : null}

        <form
          onSubmit={(e) => {
            void onResetWorkspace(e);
          }}
          className="flex flex-col gap-3 sm:flex-row sm:items-end"
        >
          <label className="block flex-1 text-sm">
            <span className="text-taupe-700 mb-1 block text-[11px] font-semibold uppercase tracking-[0.12em]">
              Type the workspace folder name to confirm
            </span>
            <input
              type="text"
              value={resetConfirm}
              onChange={(e) => setResetConfirm(e.target.value)}
              disabled={resetBusy}
              data-test="admin-reset-workspace-confirm-input"
              className="bg-cream-50 border-taupe-500 text-fg focus:border-audi-red w-full rounded-none border px-3 py-2 focus:outline-none focus:ring-1 focus:ring-audi-red"
              placeholder="e.g. debug-workspace"
              required
              minLength={1}
              maxLength={256}
            />
          </label>
          <button
            type="submit"
            disabled={resetBusy || resetConfirm.length === 0}
            data-test="admin-reset-workspace-submit"
            className="border-audi-red text-audi-red hover:bg-audi-red rounded-none border px-4 py-2 text-xs font-semibold uppercase tracking-wide hover:text-white disabled:cursor-not-allowed disabled:opacity-30"
          >
            {resetBusy ? 'Resetting…' : 'Reset workspace'}
          </button>
        </form>
      </section>
    </div>
  );
}
